import { Question } from '../types';
import { questions } from '../data/questions';

import { formatDate } from './dates';

export interface CategoryScore {
  category: string;
  score: number;
  grade: string;
  maxScore: number;
  percentage: number;
}

export interface AssessmentResult {
  totalScore: number;
  maxScore: number;
  overallGrade: string;
  categoryScores: CategoryScore[];
  strengths: CategoryScore[];
  growthAreas: CategoryScore[];
  averageScore: number;
  completionDate: string;
}

interface GradeThreshold {
  min: number;
  grade: string;
  label: string;
}

const GRADE_THRESHOLDS: GradeThreshold[] = [
  { min: 90, grade: 'A', label: 'Excellent - Outstanding operational efficiency' },
  { min: 80, grade: 'B+', label: 'Very Good - Strong operations with minor improvements needed' },
  { min: 70, grade: 'B', label: 'Good - Solid foundation with room for enhancement' },
  { min: 60, grade: 'C+', label: 'Fair - Basic systems in place but significant improvement needed' },
  { min: 50, grade: 'C', label: 'Needs Improvement - Multiple areas require attention' },
  { min: 0, grade: 'D', label: 'Requires Immediate Attention - Fundamental operational changes needed' }
];

function validateScore(score: number, questionId: string): number {
  if (!Number.isFinite(score) || score < 1 || score > 10) {
    console.warn(`Invalid score ${score} for question ${questionId}. Clamping to valid range.`);
    return Math.max(1, Math.min(10, score));
  }
  return score;
}

function getGradeForPercentage(percentage: number): { grade: string; label: string } {
  const threshold = GRADE_THRESHOLDS.find(t => percentage >= t.min) 
    ?? GRADE_THRESHOLDS[GRADE_THRESHOLDS.length - 1];
  return { grade: threshold.grade, label: threshold.label };
}

function calculateCategoryScore(
  responses: Record<string, number>,
  category: string
): CategoryScore {
  const categoryQuestions = questions.filter(q => q.category === category);
  const maxPossibleScore = categoryQuestions.length * 10;
  
  let totalScore = 0;
  let answeredQuestions = 0;

  categoryQuestions.forEach(question => {
    const score = responses[question.id];
    if (typeof score === 'number') {
      totalScore += validateScore(score, question.id);
      answeredQuestions++;
    }
  });

  const percentage = answeredQuestions > 0
    ? Math.round((totalScore / (answeredQuestions * 10)) * 100)
    : 0;

  const { grade, label } = getGradeForPercentage(percentage);

  return {
    category,
    score: totalScore,
    maxScore: maxPossibleScore,
    percentage,
    grade: `${grade} (${label})`
  };
}

export function calculateResults(responses: Record<string, number>): AssessmentResult {
  console.log('calculateResults - Input responses:', {
    totalResponses: Object.keys(responses).length,
    responses
  });

  // Get unique categories from questions
  const categories = [...new Set(questions.map(q => q.category))];
  const now = new Date();
  
  // Calculate scores for each category
  const categoryScores = categories.map(category => {
    const score = calculateCategoryScore(responses, category);
    console.log('calculateResults - Category score:', {
      category,
      score: score.score,
      maxScore: score.maxScore,
      percentage: score.percentage
    });
    return score;
  });

  // Calculate total scores
  const totalScore = categoryScores.reduce((sum, cat) => sum + cat.score, 0);
  const maxScore = categoryScores.reduce((sum, cat) => sum + cat.maxScore, 0);
  const averagePercentage = Math.round((totalScore / maxScore) * 100);

  console.log('calculateResults - Final scores:', {
    totalScore,
    maxScore,
    averagePercentage
  });

  // Get overall grade
  const { grade, label } = getGradeForPercentage(averagePercentage);

  // Identify strengths and growth areas
  const strengths = categoryScores
    .filter(cat => cat.percentage >= 80)
    .sort((a, b) => b.percentage - a.percentage);

  const growthAreas = categoryScores.filter(cat => cat.percentage < 60);

  const result = {
    totalScore,
    maxScore,
    averageScore: averagePercentage,
    overallGrade: `${grade} - ${label}`,
    categoryScores: categoryScores.sort((a, b) => b.percentage - a.percentage),
    strengths,
    growthAreas,
    completionDate: formatDate(now)
  };

  console.log('calculateResults - Final result:', result);
  return result;
}